import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, useAuth } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ArrowLeft, IndianRupee, StickyNote, CalendarPlus, Plus, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import CreateTicketDialog from '../../components/CreateTicketDialog';
import StudentAccountActions from '../../components/staff/StudentAccountActions';

const NOTE_TYPES = { note: 'Note', follow_up: 'Follow-up', action_plan: 'Action Plan' };

const StudentDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  const canEdit = ['director', 'admin', 'senior_executive', 'executive'].includes(user?.role);
  const canPay = ['director', 'admin', 'senior_executive', 'executive', 'finance'].includes(user?.role);

  const loadAll = useCallback(async () => {
    try {
      const [sRes, pRes, payRes, nRes, bRes] = await Promise.all([
        axios.get(`${API}/staff/students/${id}`),
        axios.get(`${API}/staff/students/${id}/performance`),
        axios.get(`${API}/staff/students/${id}/payments`),
        axios.get(`${API}/staff/students/${id}/notes`),
        axios.get(`${API}/staff/students/${id}/bookings`),
      ]);
      setStudent(sRes.data);
      setPerformance(pRes.data);
      setPayments(payRes.data);
      setNotes(nRes.data);
      setBookings(bRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load student');
      if (e.response?.status === 403 || e.response?.status === 404) navigate('/staff/students');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadAll();
    axios.get(`${API}/teachers`).then((r) => setTeachers(r.data)).catch(() => {});
  }, [loadAll]);

  if (loading) return <div className="text-center py-20 text-slate-600">Loading...</div>;
  if (!student) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="student-detail-page">
      <Button variant="ghost" onClick={() => navigate('/staff/students')} className="mb-4" data-testid="back-to-students">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Students
      </Button>

      <Card className="border-0 shadow-lg mb-6">
        <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="student-name">
              {student.name}
              {student.is_blocked && <Badge className="bg-rose-100 text-rose-700 border-rose-300 text-sm" data-testid="student-blocked-badge">Blocked</Badge>}
            </h1>
            <p className="text-slate-500">{student.email}</p>
            {student.is_blocked && (
              <p className="text-xs text-rose-600 mt-1" data-testid="student-block-info">
                Blocked by {student.blocked_by}{student.block_reason ? ` — ${student.block_reason}` : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {student.assigned_senior_executive_name && (
              <Badge className="bg-violet-100 text-violet-700" data-testid="student-senior-badge">
                Sr. Exec: {student.assigned_senior_executive_name}
              </Badge>
            )}
            {student.assigned_executive_name ? (
              <Badge className="bg-indigo-100 text-indigo-700">Exec: {student.assigned_executive_name}</Badge>
            ) : (
              <Badge variant="outline">
                {student.assigned_senior_executive_name ? 'Awaiting Executive' : 'Unassigned'}
              </Badge>
            )}
            <CreateTicketDialog presetStudentId={id} presetStudentName={student.name} />
            {['director', 'admin'].includes(user?.role) && <StudentAccountActions student={student} onChanged={loadAll} />}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          {canEdit && <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>}
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
        </TabsList>

        {/* Overview / Performance */}
        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <PerfCard label="Tests Taken" value={performance?.total_tests ?? 0} />
            <PerfCard label="Accuracy" value={`${performance?.accuracy ?? 0}%`} />
            <PerfCard label="SIS Score" value={performance?.sis_score != null ? performance.sis_score : '—'} />
            <PerfCard label="Study Time" value={`${performance?.total_time_minutes ?? 0}m`} />
          </div>
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-indigo-600" />Learning Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InsightRow label="Weak Subjects" hasData={(performance?.weak_subjects || []).length > 0}>
                {(performance?.weak_subjects || []).map((it, i) => (
                  <Badge key={`ws-${it}-${i}`} variant="secondary" className="bg-slate-100 text-slate-700">{it}</Badge>
                ))}
              </InsightRow>
              <InsightRow label="Topics to Improve" hasData={(performance?.top_topics_to_improve || []).length > 0}>
                {(performance?.top_topics_to_improve || []).map((it, i) => (
                  <Badge key={`tt-${it}-${i}`} variant="secondary" className="bg-slate-100 text-slate-700">{it}</Badge>
                ))}
              </InsightRow>
              {performance?.error_personality && (
                <p className="text-sm text-slate-600"><span className="font-medium">Error Personality:</span> {performance.error_personality}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments">
          <div className="flex justify-end mb-3">
            {canPay && <AddPaymentDialog studentId={id} onDone={loadAll} />}
          </div>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-0">
              {payments.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No payment records yet.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-4" data-testid={`payment-row-${p.id}`}>
                      <div>
                        <div className="font-semibold text-slate-900">₹{p.amount.toFixed(0)} <span className="text-xs font-normal text-slate-400">via {p.method}</span></div>
                        <div className="text-sm text-slate-500">{p.plan || 'Payment'} • {p.paid_on}</div>
                        {p.note && <div className="text-xs text-slate-400 mt-1">{p.note}</div>}
                        <div className="text-xs text-slate-400">Recorded by {p.recorded_by_name}</div>
                      </div>
                      <Badge className={p.status === 'received' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {p.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes */}
        {canEdit && (
          <TabsContent value="notes">
            <div className="flex justify-end mb-3">
              <AddNoteDialog studentId={id} onDone={loadAll} />
            </div>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-0">
                {notes.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No notes yet.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {notes.map((n) => (
                      <div key={n.id} className="p-4" data-testid={`note-row-${n.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="bg-violet-50 text-violet-700">{NOTE_TYPES[n.type] || n.type}</Badge>
                          {n.follow_up_date && <span className="text-xs text-amber-600">Follow-up: {n.follow_up_date}</span>}
                        </div>
                        <p className="text-sm text-slate-700">{n.content}</p>
                        <div className="text-xs text-slate-400 mt-1">{n.created_by_name} • {(n.created_at || '').slice(0, 10)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Bookings */}
        <TabsContent value="bookings">
          <div className="flex justify-end mb-3">
            {canEdit && (
              <AddBookingDialog
                studentId={id}
                teacherOptions={teachers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} (₹{t.rate_per_hour}/hr)</SelectItem>
                ))}
                onDone={loadAll}
              />
            )}
          </div>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-0">
              {bookings.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No bookings yet.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {bookings.map((b) => (
                    <div key={b.id} className="flex items-center justify-between p-4" data-testid={`booking-row-${b.id}`}>
                      <div>
                        <div className="font-semibold text-slate-900">{b.subject} with {b.teacher_name}</div>
                        <div className="text-sm text-slate-500">{b.booking_date} at {b.start_time} • {b.duration_hours}h • ₹{b.total_amount}</div>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="outline">{b.booking_status}</Badge>
                        <Badge className={b.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{b.payment_status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const PerfCard = ({ label, value }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-4 text-center">
      <div className="text-2xl font-bold text-indigo-600">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </CardContent>
  </Card>
);

const InsightRow = ({ label, hasData, children }) => (
  <div>
    <p className="text-sm font-medium text-slate-700 mb-1">{label}</p>
    {hasData ? (
      <div className="flex flex-wrap gap-2">{children}</div>
    ) : (
      <p className="text-sm text-slate-400">No data yet</p>
    )}
  </div>
);

const AddPaymentDialog = ({ studentId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: '', method: 'offline', status: 'received', plan: '', note: '' });
  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Enter a valid amount');
    try {
      await axios.post(`${API}/staff/students/${studentId}/payments`, { ...form, amount: Number(form.amount) });
      toast.success('Payment recorded');
      setOpen(false);
      setForm({ amount: '', method: 'offline', status: 'received', plan: '', note: '' });
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-payment-button"><IndianRupee className="w-4 h-4 mr-2" />Record Payment</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Record Payment (Offline)</DialogTitle><DialogDescription>Log a payment received from this student.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" /></div>
          <div>
            <Label>Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['offline', 'upi', 'cash', 'card', 'bank_transfer'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Plan / Description</Label><Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="payment-plan-input" /></div>
          <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="payment-note-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="payment-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddNoteDialog = ({ studentId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'note', content: '', follow_up_date: '' });
  const submit = async () => {
    if (!form.content.trim()) return toast.error('Enter note content');
    try {
      await axios.post(`${API}/staff/students/${studentId}/notes`, form);
      toast.success('Note added');
      setOpen(false);
      setForm({ type: 'note', content: '', follow_up_date: '' });
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-note-button"><StickyNote className="w-4 h-4 mr-2" />Add Note</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Note / Follow-up / Action Plan</DialogTitle><DialogDescription>Record a note, follow-up reminder or action plan for this student.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
                <SelectItem value="action_plan">Action Plan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Content</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="note-content-input" /></div>
          <div><Label>Follow-up Date (optional)</Label><Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} data-testid="note-followup-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="note-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddBookingDialog = ({ studentId, teacherOptions, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ teacher_id: '', subject: '', booking_date: '', start_time: '', duration_hours: '1' });
  const submit = async () => {
    if (!form.teacher_id || !form.subject || !form.booking_date || !form.start_time) {
      return toast.error('Fill all required fields');
    }
    try {
      await axios.post(`${API}/staff/students/${studentId}/bookings`, { ...form, duration_hours: Number(form.duration_hours) });
      toast.success('Booking created');
      setOpen(false);
      setForm({ teacher_id: '', subject: '', booking_date: '', start_time: '', duration_hours: '1' });
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create booking');
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-booking-button"><CalendarPlus className="w-4 h-4 mr-2" />Manual Booking</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Manual Booking</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Teacher</Label>
            <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
              <SelectTrigger data-testid="booking-teacher-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
              <SelectContent>
                {teacherOptions}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="booking-subject-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.booking_date} onChange={(e) => setForm({ ...form, booking_date: e.target.value })} data-testid="booking-date-input" /></div>
            <div><Label>Time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="booking-time-input" /></div>
          </div>
          <div><Label>Duration (hours)</Label><Input type="number" min="0.5" step="0.5" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} data-testid="booking-duration-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="booking-submit-button"><Plus className="w-4 h-4 mr-1" />Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StudentDetail;
